import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Slide } from "@/types";

interface HeroCarouselProps {
  slides: Slide[];
}

const DETAIL_PREFIX: Record<Slide["type"], string> = {
  movie: "/detail/movie",
  novel: "/detail/novel",
  news: "/detail/news",
};

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % slides.length) + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5200);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [slides.length]);

  const pause = () => {
    if (timer.current) window.clearInterval(timer.current);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      goTo(diff > 0 ? index - 1 : index + 1);
    }
    touchStartX.current = null;
  };

  if (!slides.length) return null;

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-secondary-900"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={pause}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-xl">
            {slides.map((slide, i) => (
              <div
                key={slide.id}
                className={`transition-all duration-500 ${
                  i === index
                    ? "block opacity-100 translate-y-0"
                    : "hidden opacity-0 translate-y-4"
                }`}
              >
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-label font-bold bg-accent-500 text-foreground-950 mb-4">
                  <i className="ri-fire-fill"></i>
                  {slide.badge}
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight drop-shadow">
                  {slide.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-lg">
                  {slide.subtitle}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`${DETAIL_PREFIX[slide.type]}/${slide.targetId}`)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    立即查看
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/category/${slide.type}`)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-medium border border-background-50/40 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    浏览更多
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-2 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}